import { type ReactNode } from 'react'
import { Link } from 'react-router'
import {
  ArchiveIcon,
  BuildingIcon,
  CalendarDotsIcon,
  CheckCircleIcon,
  ClipboardTextIcon,
  DropIcon,
  FlagIcon,
  HandshakeIcon,
  MapPinIcon,
  NumberCircleOneIcon,
  ScalesIcon,
  SlidersHorizontalIcon,
  TargetIcon,
  TestTubeIcon,
  UserIcon,
  VirusIcon
} from '@phosphor-icons/react'
import * as Mtds from '@mattilsynet/design/react'
import { PlanStatusTag } from '../plan-status-tag/PlanStatusTag'
import { SubstanceTags } from '../substance-tags/SubstanceTags'
import {
  ScreenHeader,
  type ScreenHeaderProperty
} from '../screen-header/ScreenHeader'
import { ScreenLayout } from '../screen-layout/ScreenLayout'
import { DetailsCard } from '../details-card/DetailsCard'
import type { Task } from '../../types/types'
import { formatMatrixAmount } from '../../utils/taskUtils'

type TaskScreenContentProps = {
  title: ReactNode
  titleActions?: ReactNode
  actions?: ReactNode
  headerProperties: ScreenHeaderProperty[]
  detailProperties: ScreenHeaderProperty[]
  labDetails?: ReactNode
  additionalDetails?: ReactNode
  mainMaxWidth?: number
  children: ReactNode
}

type TaskHeaderPropertiesOptions = {
  task: Task
  samplesTakenValue?: ReactNode
  statusValue?: ReactNode
  sampntTypeValue?: ReactNode
  duedatesValue?: ReactNode
  matrixAmountValue?: ReactNode
}

type TaskDetailProgressProperty = {
  label: string
  value: ReactNode
}

type TaskDetailPropertiesOptions = {
  task: Task
  planValue?: ReactNode
  contractValue?: ReactNode
  approvalStatusValue?: ReactNode
  progressProperty?: TaskDetailProgressProperty
  quantityValue?: ReactNode
  countSubSamplesValue?: ReactNode
  locationRequiredValue?: ReactNode
  archiveCategoryValue?: ReactNode
  purposeValue?: ReactNode
  okProgramValue?: ReactNode
  subPurposeValue?: ReactNode
  ownerValue?: ReactNode
  ownerLabel?: string
  schemaValue?: ReactNode
  planLinkTo?: string
}

const fallback = (value: ReactNode): ReactNode => value ?? '-'

const purposeDetailLabels = new Set(['Hensikt', 'OK-program', 'Underhensikt'])
const labDetailLabels = new Set(['Godkjenning', 'Kontrakt', 'Laboratorium'])

export const AnalysisTypeApprovalStatusTag = ({
  status,
  approvalComment
}: {
  status: Task['analysisTypeApprovalStatus']
  approvalComment?: string
}) => {
  if (status == null) return null

  const meta: Record<
    NonNullable<Task['analysisTypeApprovalStatus']>,
    { color: 'success' | 'danger' | 'warning'; label: string }
  > = {
    approved: { color: 'success', label: 'Godkjent' },
    declined: { color: 'danger', label: 'Avvist' },
    awaiting: { color: 'warning', label: 'Avventer' }
  }
  const { color, label } = meta[status]

  return (
    <Mtds.Tag data-color={color} data-tooltip={approvalComment ?? undefined}>
      {label}
    </Mtds.Tag>
  )
}

export const getTaskHeaderProperties = ({
  task,
  samplesTakenValue,
  statusValue,
  sampntTypeValue,
  duedatesValue,
  matrixAmountValue
}: TaskHeaderPropertiesOptions): ScreenHeaderProperty[] => {
  const properties: ScreenHeaderProperty[] = []

  if (task.planStatus) {
    properties.push({
      label: 'Status',
      icon: <FlagIcon aria-hidden />,
      value: statusValue ?? <PlanStatusTag status={task.planStatus} />
    })
  }

  properties.push(
    {
      label: 'Produkt',
      icon: <DropIcon aria-hidden />,
      value: task.analysisType?.sampleType?.product ?? '-'
    },
    {
      label: 'Prøve av',
      icon: <DropIcon aria-hidden />,
      value: task.analysisType?.sampleType?.matrix ?? '-'
    },
    {
      label: 'Mengde prøvemateriale',
      icon: <ScalesIcon aria-hidden />,
      value: matrixAmountValue ?? formatMatrixAmount(task.matrixAmount) ?? '-'
    },
    {
      label: 'Analyser for',
      icon: <VirusIcon aria-hidden />,
      value: <SubstanceTags analysisType={task.analysisType} />
    },
    {
      label: 'Uttakssted',
      icon: <MapPinIcon aria-hidden />,
      value: sampntTypeValue ?? task.sampntType?.nameNo ?? '-'
    },
    ...(duedatesValue !== undefined
      ? [
          {
            label: 'Frister',
            icon: <CalendarDotsIcon aria-hidden />,
            value: duedatesValue
          }
        ]
      : []),
    {
      label: 'Prøver tatt',
      icon: <TestTubeIcon aria-hidden />,
      value:
        samplesTakenValue ??
        (task.totalCount != null && task.quantity != null
          ? `${task.totalCount} av ${task.quantity}`
          : '-')
    }
  )

  return properties
}

export const getTaskDetailProperties = ({
  task,
  planValue,
  contractValue,
  approvalStatusValue,
  progressProperty,
  quantityValue,
  countSubSamplesValue,
  locationRequiredValue,
  archiveCategoryValue,
  purposeValue,
  okProgramValue,
  subPurposeValue,
  ownerValue,
  ownerLabel = 'Eier',
  schemaValue,
  planLinkTo
}: TaskDetailPropertiesOptions): ScreenHeaderProperty[] => {
  const properties: ScreenHeaderProperty[] = []

  if (planValue !== undefined) {
    properties.push({
      label: 'Plan',
      icon: <NumberCircleOneIcon aria-hidden />,
      value: fallback(planValue)
    })
  }

  if (contractValue !== undefined) {
    properties.push({
      label: 'Kontrakt',
      icon: <HandshakeIcon aria-hidden />,
      value: fallback(contractValue)
    })
  }

  properties.push({
    label: 'Laboratorium',
    icon: <BuildingIcon aria-hidden />,
    value: task.lab?.name ?? '-'
  })

  if (approvalStatusValue !== undefined) {
    properties.push({
      label: 'Godkjenning',
      icon: <CheckCircleIcon aria-hidden />,
      value: fallback(approvalStatusValue)
    })
  }

  if (progressProperty !== undefined) {
    properties.push({
      label: progressProperty.label,
      icon: <NumberCircleOneIcon aria-hidden />,
      value: fallback(progressProperty.value)
    })
  }

  properties.push({
    label: 'Antall prøver',
    icon: <TestTubeIcon aria-hidden />,
    value: fallback(quantityValue ?? task.quantity)
  })

  properties.push(
    {
      label: 'Hvordan telle prøver',
      icon: <SlidersHorizontalIcon aria-hidden />,
      value:
        countSubSamplesValue ??
        (task.countSubSamples ? 'Delprøver' : 'Prøveposer')
    },
    {
      label: 'Krever nøyaktig posisjon',
      icon: <MapPinIcon aria-hidden />,
      value: locationRequiredValue ?? (task.locationRequired ? 'Ja' : 'Nei')
    }
  )

  if (archiveCategoryValue !== undefined) {
    properties.push({
      label: 'Arkivkategori',
      icon: <ArchiveIcon aria-hidden />,
      value: fallback(archiveCategoryValue)
    })
  }

  if (purposeValue !== undefined) {
    properties.push({
      label: 'Hensikt',
      icon: <TargetIcon aria-hidden />,
      value: (
        <Mtds.Flex data-align='center' data-gap='2'>
          {fallback(purposeValue)}
          {planLinkTo ? (
            <Link to={planLinkTo} aria-label='Gå til plan'>
              <Mtds.Tag data-color='neutral' data-icon='none'>
                Plan
              </Mtds.Tag>
            </Link>
          ) : (
            <Mtds.Tag data-color='neutral' data-icon='none'>
              Plan
            </Mtds.Tag>
          )}
        </Mtds.Flex>
      )
    })
  }

  if (okProgramValue !== undefined) {
    properties.push({
      label: 'OK-program',
      icon: <TargetIcon aria-hidden />,
      value: fallback(okProgramValue)
    })
  }

  if (subPurposeValue !== undefined) {
    properties.push({
      label: 'Underhensikt',
      icon: <TargetIcon aria-hidden />,
      value: fallback(subPurposeValue)
    })
  }

  if (ownerValue !== undefined) {
    properties.push({
      label: ownerLabel,
      icon: <UserIcon aria-hidden />,
      value: fallback(ownerValue)
    })
  }

  if (schemaValue !== undefined) {
    properties.push({
      label: 'Skjema',
      icon: <ClipboardTextIcon aria-hidden />,
      value: fallback(schemaValue)
    })
  }

  return properties
}

export const TaskScreenContent = ({
  title,
  titleActions,
  actions,
  headerProperties,
  detailProperties,
  labDetails,
  additionalDetails,
  mainMaxWidth,
  children
}: TaskScreenContentProps) => {
  const purposeProperties = detailProperties.filter((property) =>
    purposeDetailLabels.has(property.label)
  )
  const labProperties = detailProperties.filter((property) =>
    labDetailLabels.has(property.label)
  )
  const otherProperties = detailProperties.filter(
    (property) =>
      !purposeDetailLabels.has(property.label) &&
      !labDetailLabels.has(property.label)
  )

  return (
    <ScreenLayout
      header={
        <ScreenHeader
          title={title}
          titleActions={titleActions}
          actions={actions}
          properties={headerProperties}
        />
      }
      details={
        <Mtds.Grid data-gap='4'>
          <DetailsCard
            title='Hensikt'
            icon={<TargetIcon aria-hidden />}
            properties={purposeProperties}
          />
          <DetailsCard
            title='Laboratorium'
            icon={<BuildingIcon aria-hidden />}
            properties={labProperties}
          >
            {labDetails && (
              <div className='grid grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)] gap-[var(--mtds-4)]'>
                <div className='col-start-2 min-w-0'>{labDetails}</div>
              </div>
            )}
          </DetailsCard>
          <DetailsCard
            title='Detaljer'
            icon={<ClipboardTextIcon aria-hidden />}
            properties={otherProperties}
          />
          {additionalDetails}
        </Mtds.Grid>
      }
      mainMaxWidth={mainMaxWidth}
    >
      {children}
    </ScreenLayout>
  )
}
